import { buttonClasses } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  loadingButtonClasses,
  LoadingButtonProps,
} from '@mui/lab/LoadingButton';
import { COLORS_SCHEMA } from '../../palette';

// Mirrors the per-size heights set in the Button override so the loading
// state stays at least square (≈ button height) when children resolve to
// empty — keeps the absolute-positioned center spinner from collapsing the
// button into a thin pill.
const SIZE_TO_HEIGHT: Record<string, number> = {
  xxsmall: 16,
  xsmall: 24,
  small: 32,
  medium: 40,
  large: 48,
  xlarge: 56,
};

export default function LoadingButton(theme: Theme) {
  const isLight = theme.palette.mode === 'light';

  const rootStyles = (ownerState: LoadingButtonProps) => {
    const inheritColor = ownerState.color === 'inherit';
    if (!ownerState.color) ownerState.color = 'default';

    const containedVariant = ownerState.variant === 'contained';

    const outlinedVariant = ownerState.variant === 'outlined';

    const textVariant = ownerState.variant === 'text';

    const softVariant = ownerState.variant === 'soft';

    const smallSize = ownerState.size === 'small';

    const loadingMinWidth =
      SIZE_TO_HEIGHT[(ownerState.size as string) ?? 'medium'] ?? 40;

    const defaultStyle = {
      ...(textVariant && {
        [`& .${loadingButtonClasses.loadingIndicatorStart}`]: {
          left: 10,
        },
        [`& .${loadingButtonClasses.loadingIndicatorEnd}`]: {
          right: 14,
        },
        ...(smallSize && {
          [`& .${loadingButtonClasses.loadingIndicatorStart}`]: {
            left: 10,
          },
          [`& .${loadingButtonClasses.loadingIndicatorEnd}`]: {
            right: 10,
          },
        }),
      }),
    };

    // Only re-specify what MUI's built-in `&.Mui-disabled` rule in
    // @mui/material/Button.js actively breaks during loading: contained bg,
    // outlined border, soft bg, and the spinner colour. Other properties
    // (text bg, text/soft border, outlined bg) already resolve correctly via
    // the Button override's disabled state.
    const inheritStyle =
      inheritColor && containedVariant
        ? {
            backgroundColor: !isLight
              ? theme.palette.grey[800]
              : theme.palette.common.white,
            [`& .${loadingButtonClasses.loadingIndicator}`]: {
              color: !isLight
                ? theme.palette.common.white
                : theme.palette.grey[800],
            },
          }
        : {};

    const colorVariantStyle = Object.values(COLORS_SCHEMA)
      .map((color) => ({
        ...(ownerState.color === color && {
          // CONTAINED
          ...(containedVariant && {
            backgroundColor: theme.palette[color][500],
            border: `1px solid ${theme.palette[color][500]}`,
            [`& .${loadingButtonClasses.loadingIndicator}`]: {
              color: theme.palette[color].contrastText,
            },
          }),
          // SOFT
          ...(softVariant && {
            backgroundColor: theme.palette[color][100],
            [`& .${loadingButtonClasses.loadingIndicator}`]: {
              color: theme.palette[color][600],
            },
          }),
          // OUTLINED
          ...(outlinedVariant && {
            border: `1px solid ${
              color === 'default'
                ? theme.palette.grey[400]
                : theme.palette[color][500]
            }`,
            [`& .${loadingButtonClasses.loadingIndicator}`]: {
              color:
                color === 'default'
                  ? theme.palette.grey[700]
                  : theme.palette[color][500],
            },
          }),
          // TEXT
          ...(textVariant && {
            [`& .${loadingButtonClasses.loadingIndicator}`]: {
              color:
                color === 'default'
                  ? theme.palette.grey[700]
                  : theme.palette[color][500],
            },
          }),
        }),
      }))
      .reduce((acc, style) => ({ ...acc, ...style }), {});

    return {
      ...defaultStyle,
      [`&.${loadingButtonClasses.loading}.${buttonClasses.disabled}`]: {
        minWidth: loadingMinWidth,
        ...inheritStyle,
        ...colorVariantStyle,
      },
    };
  };

  return {
    MuiLoadingButton: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: LoadingButtonProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
